import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { t } from "@/i18n-js/instance";
import { useAiWorkflowsBillingHistory } from "@/react/hooks/useAiWorkflowsBillingHistory";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { DataTable } from "@circle-react-uikit/DataTableV2";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { useAiActionsBilling } from "../hooks/useAiActionsBilling";
import type { DateRange } from "../hooks/useDateRanges";
import {
  //renderCharges,
  renderCreatedAt,
  renderCredits,
  renderTotalRuns,
  renderWorkflowName,
} from "./WorkflowUsageTableCells";

interface WorkflowUsageModalProps {
  dateRange: DateRange;
  freeQuantity: number;
}

interface WorkflowBillingData {
  created_at: string;
  total_credits_consumed: number;
  total_overage_charges: number;
  total_runs: number;
  workflow_id: string;
  workflow_name: string;
}

const WorkflowUsageModalComponent = ({
  dateRange,
  freeQuantity,
}: WorkflowUsageModalProps) => {
  const modal = useModal();
  const { billingDetails } = useAiActionsBilling();

  // Calculate date range based on selected billing cycle
  const { startDate, endDate } = useMemo(() => {
    if (!dateRange || dateRange.startDate === 0 || dateRange.endDate === 0) {
      return { startDate: "", endDate: "" };
    }

    const startDateObj = new Date(dateRange.startDate * 1000);
    const endDateObj = new Date(dateRange.endDate * 1000);

    return {
      startDate: format(startDateObj, "yyyy-MM-dd"),
      endDate: format(endDateObj, "yyyy-MM-dd"),
    };
  }, [dateRange]);

  const {
    workflows,
    pagination,
    isLoading,
    error,
    page,
    sortField,
    sortDirection,
    handlePageChange,
    handleSortChange,
  } = useAiWorkflowsBillingHistory({
    start_date: startDate,
    end_date: endDate,
    free_quantity: freeQuantity,
    price_overage: billingDetails?.price_overage || 0,
    enabled: modal.visible && !!startDate && !!endDate,
  });

  const handleClose = () => {
    void modal.hide();
    modal.remove();
  };

  // Column definitions
  const columns: ColumnDef<WorkflowBillingData>[] = useMemo(
    () => [
      {
        accessorKey: "workflow_name",
        header: t("settings.billing.ai_actions.table_headers.name"),
        cell: info => renderWorkflowName(info.getValue() as string),
      },
      {
        accessorKey: "total_credits_consumed",
        header: t("settings.billing.ai_actions.table_headers.credits"),
        cell: info => renderCredits(info.getValue() as number),
      },
      // {
      //   accessorKey: "total_overage_charges",
      //   header: t("settings.billing.ai_actions.table_headers.charges"),
      //   cell: info => renderCharges(info.getValue() as number),
      // },
      {
        accessorKey: "total_runs",
        header: t("settings.billing.ai_actions.table_headers.total_runs"),
        cell: info => renderTotalRuns(info.getValue() as number),
      },
      {
        accessorKey: "created_at",
        header: t("settings.billing.ai_actions.table_headers.created_at"),
        cell: info => renderCreatedAt(info.getValue() as string),
      },
    ],
    [],
  );

  return (
    <Modal isOpen={modal.visible} onClose={handleClose}>
      <Modal.Overlay />
      <Modal.Content size="full" className="max-w-screen-xl">
        <Modal.Header>
          <Modal.Title>
            {t("settings.billing.ai_actions.modal_title")}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={handleClose} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="flex h-full flex-col p-8">
          {error ? (
            <div className="flex h-full items-center justify-center">
              <Typography.BodyMd color="text-danger">
                {t("settings.billing.ai_actions.error_loading_data")}
              </Typography.BodyMd>
            </div>
          ) : (
            <DataTable
              data={workflows}
              columns={columns}
              totalRecordsCount={pagination.total_count}
              isLoading={isLoading}
              onChangePage={handlePageChange}
              pageSize={pagination.per_page}
              initialPage={page}
              onChangeSorting={sorting => {
                if (sorting.length > 0) {
                  handleSortChange(sorting[0].id);
                }
              }}
              initialSort={[{ id: sortField, desc: sortDirection === "desc" }]}
            >
              <DataTable.TableWrapper className="px-0">
                <DataTable.Table>
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "25%" }} />
                    {/* <col style={{ width: "15%" }} /> */}
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <DataTable.SortableHead />
                  <DataTable.Loader />
                  <DataTable.NoRecords
                    text={t("settings.billing.ai_actions.no_workflows_found")}
                  />
                  <DataTable.Body />
                </DataTable.Table>
                <DataTable.Pagination align="left" direction="reverse" />
              </DataTable.TableWrapper>
            </DataTable>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

const WorkflowUsageModalDrawer = ModalManager.create(
  ({ dateRange, freeQuantity }: WorkflowUsageModalProps) => (
    <WorkflowUsageModalComponent
      dateRange={dateRange}
      freeQuantity={freeQuantity}
    />
  ),
);

export const useWorkflowUsageModal = () => useModal(WorkflowUsageModalDrawer);
